/**
 * Campaign Flow Utilities
 * 
 * Provides helper functions for creating and validating campaign flows
 */

import { nanoid } from 'nanoid';
import {
  CampaignFlow,
  FlowStep,
  StaticPage,
  STEP_ORDERING_RULES,
  StepType,
} from '../types/campaign-flow.types';

/**
 * Default content translations for all step types
 * Based on existing i18n translations to ensure seamless migration
 */
const DEFAULT_CONTENT = {
  landing: {
    title: {
      'en-us': 'Are you ready to win?',
      'fr-ca': 'Êtes-vous prêt à gagner?',
    },
    subtitle: {
      'en-us': 'Get ready for an amazing experience',
      'fr-ca': 'Préparez-vous pour une expérience incroyable',
    },
    bodyText: {
      'en-us': 'To continue, please allow camera permissions and acknowledge the terms and conditions',
      'fr-ca': 'Pour continuer, veuillez autoriser les permissions de caméra et accepter les conditions générales',
    },
    ctaButtonText: {
      'en-us': 'Ready',
      'fr-ca': 'Prêt',
    },
    legalText: {
      'en-us': 'By clicking "Ready", you acknowledge that you have read and agree to the Terms of Service and Privacy Policy.',
      'fr-ca': 'En cliquant sur "Prêt", vous reconnaissez avoir lu et accepté les Conditions d\'utilisation et la Politique de confidentialité.',
    },
  },
  play: {
    wheel: {
      title: {
        'en-us': 'Spin to Win!',
        'fr-ca': 'Tournez pour gagner!',
      },
      subtitle: {
        'en-us': 'Point your camera at the QR code to begin',
        'fr-ca': 'Pointez votre caméra vers le code QR pour commencer',
      },
      ctaButtonText: {
        'en-us': 'Spin',
        'fr-ca': 'Tourner',
      },
      arTrackingMessage: {
        'en-us': 'Please keep the code in frame',
        'fr-ca': 'Veuillez garder le code dans le cadre',
      },
      orientationMessage: {
        'en-us': 'Please rotate your device to landscape orientation',
        'fr-ca': 'Veuillez faire pivoter votre appareil en orientation paysage',
      },
      permissionDeniedTitle: {
        'en-us': 'Camera Access Required',
        'fr-ca': 'Accès à la caméra requis',
      },
      permissionDeniedBody: {
        'en-us': 'We need access to your camera to scan the QR code and play the game.',
        'fr-ca': 'Nous avons besoin d\'accéder à votre caméra pour scanner le code QR et jouer au jeu.',
      },
      permissionInstructions: {
        'en-us': 'To enable camera access, go to your device settings and allow camera permissions for this browser.',
        'fr-ca': 'Pour activer l\'accès à la caméra, accédez aux paramètres de votre appareil et autorisez les autorisations de caméra pour ce navigateur.',
      },
    },
    scratchcard: {
      title: {
        'en-us': 'Scratch & Win!',
        'fr-ca': 'Grattez et gagnez!',
      },
      subtitle: {
        'en-us': 'Point your camera at the QR code to begin',
        'fr-ca': 'Pointez votre caméra vers le code QR pour commencer',
      },
      ctaButtonText: {
        'en-us': 'Scratch',
        'fr-ca': 'Gratter',
      },
      arTrackingMessage: {
        'en-us': 'Please keep the code in frame',
        'fr-ca': 'Veuillez garder le code dans le cadre',
      },
      orientationMessage: {
        'en-us': 'Please rotate your device to landscape orientation',
        'fr-ca': 'Veuillez faire pivoter votre appareil en orientation paysage',
      },
      permissionDeniedTitle: {
        'en-us': 'Camera Access Required',
        'fr-ca': 'Accès à la caméra requis',
      },
      permissionDeniedBody: {
        'en-us': 'We need access to your camera to scan the QR code and play the game.',
        'fr-ca': 'Nous avons besoin d\'accéder à votre caméra pour scanner le code QR et jouer au jeu.',
      },
      permissionInstructions: {
        'en-us': 'To enable camera access, go to your device settings and allow camera permissions for this browser.',
        'fr-ca': 'Pour activer l\'accès à la caméra, accédez aux paramètres de votre appareil et autorisez les autorisations de caméra pour ce navigateur.',
      },
    },
  },
  win: {
    title: {
      'en-us': 'Congratulations!',
      'fr-ca': 'Félicitations!',
    },
    subtitle: {
      'en-us': 'You have won:',
      'fr-ca': 'Vous avez gagné:',
    },
    bodyText: {
      'en-us': 'Please enter your email or phone number below to claim your prize',
      'fr-ca': 'Veuillez entrer votre courriel ou numéro de téléphone ci-dessous pour réclamer votre prix',
    },
    ctaButtonText: {
      'en-us': 'Submit',
      'fr-ca': 'Soumettre',
    },
    formFieldLabel: {
      'en-us': 'Email or Phone Number',
      'fr-ca': 'Courriel ou numéro de téléphone',
    },
    formErrorMessage: {
      'en-us': 'Please enter a valid email address or 10-digit phone number',
      'fr-ca': 'Veuillez entrer une adresse courriel valide ou un numéro de téléphone à 10 chiffres',
    },
    smsConsentText: {
      'en-us': 'By checking this box and clicking Submit, you consent to receive SMS messages. Message and data rates may apply.',
      'fr-ca': 'En cochant cette case et en cliquant sur Soumettre, vous consentez à recevoir des messages SMS. Des frais de messagerie et de données peuvent s\'appliquer.',
    },
    smsDisabledText: {
      'en-us': 'SMS consent is only available for phone numbers',
      'fr-ca': 'Le consentement SMS n\'est disponible que pour les numéros de téléphone',
    },
    privacyLinkText: {
      'en-us': 'Privacy Policy',
      'fr-ca': 'Politique de confidentialité',
    },
  },
  redeem: {
    title: {
      'en-us': 'Claim Your Prize',
      'fr-ca': 'Réclamez votre prix',
    },
    bodyText: {
      'en-us': 'Please proceed to the loyalty desk and present this screen to a representative',
      'fr-ca': 'Veuillez vous rendre au bureau de fidélisation et présenter cet écran à un représentant',
    },
    ctaButtonText: {
      'en-us': 'Redeem',
      'fr-ca': 'Échanger',
    },
    redemptionInstructions: {
      'en-us': 'Show this screen to redeem your prize',
      'fr-ca': 'Montrez cet écran pour échanger votre prix',
    },
    confirmationMessage: {
      'en-us': 'Thank you for playing!',
      'fr-ca': 'Merci d\'avoir joué!',
    },
    prizeLabel: {
      'en-us': 'Prize Won:',
      'fr-ca': 'Prix gagné:',
    },
    codeLabel: {
      'en-us': 'Redemption Code:',
      'fr-ca': 'Code d\'échange:',
    },
  },
};

import type { GameType } from '../types/game-engine.types';

/**
 * Generates a default 4-step flow for new campaigns
 * 
 * @param config Configuration options
 * @returns Default campaign flow
 */
export function generateDefaultFlow(config: {
  campaignId: string;
  gameType: GameType;
}): CampaignFlow {
  const gameTypeContent = config.gameType === 'wheel' ? DEFAULT_CONTENT.play.wheel : DEFAULT_CONTENT.play.scratchcard;
  
  return {
    version: '1.0',
    steps: [
      createDefaultStep({
        type: 'landing',
        order: 0,
        content: DEFAULT_CONTENT.landing,
      }),
      createDefaultStep({
        type: 'play',
        order: 1,
        content: gameTypeContent,
      }),
      createDefaultStep({
        type: 'win',
        order: 2,
        content: DEFAULT_CONTENT.win,
      }),
      createDefaultStep({
        type: 'redeem',
        order: 3,
        content: DEFAULT_CONTENT.redeem,
      }),
    ],
  };
}

/**
 * Creates a default flow step with platform metadata
 * 
 * @param options Step creation options
 * @returns Configured flow step
 */
function createDefaultStep(options: {
  type: StepType;
  order: number;
  content: {
    title: { [locale: string]: string };
    subtitle?: { [locale: string]: string };
    bodyText?: { [locale: string]: string };
    ctaButtonText?: { [locale: string]: string };
    // Landing specific
    legalText?: { [locale: string]: string };
    // Play specific
    arTrackingMessage?: { [locale: string]: string };
    orientationMessage?: { [locale: string]: string };
    permissionDeniedTitle?: { [locale: string]: string };
    permissionDeniedBody?: { [locale: string]: string };
    permissionInstructions?: { [locale: string]: string };
    // Win specific
    formFieldLabel?: { [locale: string]: string };
    formErrorMessage?: { [locale: string]: string };
    smsConsentText?: { [locale: string]: string };
    smsDisabledText?: { [locale: string]: string };
    privacyLinkText?: { [locale: string]: string };
    // Redeem specific
    redemptionInstructions?: { [locale: string]: string };
    confirmationMessage?: { [locale: string]: string };
    prizeLabel?: { [locale: string]: string };
    codeLabel?: { [locale: string]: string };
  };
}): FlowStep {
  const { type, order, content } = options;
  
  // Build content object, only including defined fields
  const stepContent: FlowStep['content'] = {
    title: content.title,
  };
  
  // Add core optional fields if they exist
  if (content.subtitle) {
    stepContent.subtitle = content.subtitle;
  }
  if (content.bodyText) {
    stepContent.bodyText = content.bodyText;
  }
  if (content.ctaButtonText) {
    stepContent.ctaButtonText = content.ctaButtonText;
  }
  
  // Add Landing-specific fields
  if (content.legalText) {
    stepContent.legalText = content.legalText;
  }
  
  // Add Play-specific fields
  if (content.arTrackingMessage) {
    stepContent.arTrackingMessage = content.arTrackingMessage;
  }
  if (content.orientationMessage) {
    stepContent.orientationMessage = content.orientationMessage;
  }
  if (content.permissionDeniedTitle) {
    stepContent.permissionDeniedTitle = content.permissionDeniedTitle;
  }
  if (content.permissionDeniedBody) {
    stepContent.permissionDeniedBody = content.permissionDeniedBody;
  }
  if (content.permissionInstructions) {
    stepContent.permissionInstructions = content.permissionInstructions;
  }
  
  // Add Win-specific fields
  if (content.formFieldLabel) {
    stepContent.formFieldLabel = content.formFieldLabel;
  }
  if (content.formErrorMessage) {
    stepContent.formErrorMessage = content.formErrorMessage;
  }
  if (content.smsConsentText) {
    stepContent.smsConsentText = content.smsConsentText;
  }
  if (content.smsDisabledText) {
    stepContent.smsDisabledText = content.smsDisabledText;
  }
  if (content.privacyLinkText) {
    stepContent.privacyLinkText = content.privacyLinkText;
  }
  
  // Add Redeem-specific fields
  if (content.redemptionInstructions) {
    stepContent.redemptionInstructions = content.redemptionInstructions;
  }
  if (content.confirmationMessage) {
    stepContent.confirmationMessage = content.confirmationMessage;
  }
  if (content.prizeLabel) {
    stepContent.prizeLabel = content.prizeLabel;
  }
  if (content.codeLabel) {
    stepContent.codeLabel = content.codeLabel;
  }
  
  return {
    id: nanoid(),
    type,
    order,
    enabled: true,
    content: stepContent,
    meta: STEP_METADATA[type],
  };
}

/**
 * Platform-defined metadata for each step type
 */
const STEP_METADATA = {
  landing: {
    required: true,
    componentName: 'ExperienceComponent',
    icon: 'home',
  },
  play: {
    required: true,
    componentName: 'PlayComponent',
    icon: 'play_circle',
  },
  win: {
    required: false,
    componentName: 'WinComponent',
    icon: 'emoji_events',
  },
  redeem: {
    required: false,
    componentName: 'RedeemComponent',
    icon: 'redeem',
  },
} as const;

/**
 * Generates default static pages for a campaign
 * 
 * @param campaignId Parent campaign ID
 * @param defaultLocale Base language for content
 * @returns Array of default static pages
 */
export function generateDefaultStaticPages(
  campaignId: string,
  defaultLocale = 'en'
): StaticPage[] {
  return [
    {
      id: nanoid(),
      campaignId,
      type: 'privacy-policy',
      slug: 'privacy-policy',
      title: { [defaultLocale]: 'Privacy Policy' },
      content: {
        [defaultLocale]: `# Privacy Policy

Your privacy is important to us. This privacy policy explains how we collect, use, and protect your personal information.

## Information We Collect
- Contact information (email, phone number)
- Game participation data
- Device information

## How We Use Your Information
- To deliver prizes and rewards
- To improve our services
- To communicate with you about promotions

## Data Protection
We use industry-standard security measures to protect your information.

Last updated: ${new Date().toLocaleDateString()}`,
      },
      showInFooter: true,
      order: 0,
      enabled: true,
      updated: new Date(),
    },
    {
      id: nanoid(),
      campaignId,
      type: 'terms-conditions',
      slug: 'terms',
      title: { [defaultLocale]: 'Terms & Conditions' },
      content: {
        [defaultLocale]: `# Terms & Conditions

By participating in this promotion, you agree to the following terms and conditions.

## Eligibility
- Must be 18 years or older
- One entry per person
- Void where prohibited by law

## Prize Redemption
- Prizes must be claimed within 30 days
- Prizes are non-transferable
- No cash value

## Liability
Sponsor is not responsible for technical difficulties or unauthorized access.

Last updated: ${new Date().toLocaleDateString()}`,
      },
      showInFooter: true,
      order: 1,
      enabled: true,
      updated: new Date(),
    },
  ];
}

/**
 * Validates a campaign flow against ordering rules
 * 
 * @param flow Campaign flow to validate
 * @returns Validation result with errors if any
 */
export function validateCampaignFlow(flow: CampaignFlow): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  // Check required steps are present and enabled
  const enabledSteps = flow.steps.filter(s => s.enabled);
  const enabledTypes = new Set(enabledSteps.map(s => s.type));
  
  // Validate required steps
  for (const [stepType, rules] of Object.entries(STEP_ORDERING_RULES)) {
    if (rules.required && !enabledTypes.has(stepType as StepType)) {
      errors.push(`Required step '${stepType}' is missing or disabled`);
    }
  }
  
  // Validate step ordering
  const sortedSteps = [...enabledSteps].sort((a, b) => a.order - b.order);
  
  for (const step of sortedSteps) {
    const rules = STEP_ORDERING_RULES[step.type];
    if (!rules) continue;
    
    // Check order constraints
    if (rules.minOrder !== undefined && step.order < rules.minOrder) {
      errors.push(`Step '${step.type}' has order ${step.order} but minimum is ${rules.minOrder}`);
    }
    
    if (rules.maxOrder !== undefined && step.order > rules.maxOrder) {
      errors.push(`Step '${step.type}' has order ${step.order} but maximum is ${rules.maxOrder}`);
    }
    
    // Check dependency constraints
    if (rules.mustComeAfter) {
      for (const requiredType of rules.mustComeAfter) {
        const requiredStep = sortedSteps.find(s => s.type === requiredType);
        if (requiredStep && requiredStep.order >= step.order) {
          errors.push(`Step '${step.type}' must come after '${requiredType}'`);
        }
      }
    }
    
    if (rules.mustComeBefore) {
      for (const requiredType of rules.mustComeBefore) {
        const requiredStep = sortedSteps.find(s => s.type === requiredType);
        if (requiredStep && requiredStep.order <= step.order) {
          errors.push(`Step '${step.type}' must come before '${requiredType}'`);
        }
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Gets the next step in the flow after the current step
 * 
 * @param flow Campaign flow
 * @param currentStepId Current step ID
 * @returns Next step or undefined if at end
 */
export function getNextStep(
  flow: CampaignFlow,
  currentStepId: string
): FlowStep | undefined {
  const enabledSteps = flow.steps
    .filter(s => s.enabled)
    .sort((a, b) => a.order - b.order);
  
  const currentIndex = enabledSteps.findIndex(s => s.id === currentStepId);
  
  if (currentIndex === -1 || currentIndex === enabledSteps.length - 1) {
    return undefined;
  }
  
  return enabledSteps[currentIndex + 1];
}

/**
 * Gets the previous step in the flow before the current step
 * 
 * @param flow Campaign flow
 * @param currentStepId Current step ID
 * @returns Previous step or undefined if at start
 */
export function getPreviousStep(
  flow: CampaignFlow,
  currentStepId: string
): FlowStep | undefined {
  const enabledSteps = flow.steps
    .filter(s => s.enabled)
    .sort((a, b) => a.order - b.order);
  
  const currentIndex = enabledSteps.findIndex(s => s.id === currentStepId);
  
  if (currentIndex <= 0) {
    return undefined;
  }
  
  return enabledSteps[currentIndex - 1];
}
